import React, { useContext, useEffect, useState } from "react";
import DataContext from "../../../../context/DataContext";
import PhoneDisplayRow from "./PhoneDisplayRow";
import { IconButton, Tooltip } from "@mui/material";
import { ControlPointOutlined } from "@mui/icons-material";
import InlineEditPhone from "../components/InlineEditPhone";
import useService from "../../../../hooks/useService";

export default function ProviderPhones({ providerDetail, activeProvObj }) {
  const providerContactInfoRepository = useService(
    "repositories/provider-contact-info-repository"
  );
  const fieldOptionRepository = useService(
    "repositories/field-option-repository"
  );
  
  const { userRoles, loggedInUser } = useContext(DataContext);
  const [providerPhones, setProviderPhones] = useState([]);
  const [trigger, setTrigger] = useState(false);
  const [formValues, setFormValues] = useState([]);
  const [phoneTypeOptions, setPhoneTypeOptions] = useState([]);
  const [providerTypesActive, setProviderTypesActive] = useState("");
  
  const canView = userRoles.permissions.includes("provider.view_providercontactinfo");
  const canAdd = userRoles.permissions.includes("provider.add_providercontactinfo");
  const canEdit = userRoles.permissions.includes("provider.change_providercontactinfo");
  const canDelete = userRoles.permissions.includes("provider.delete_providercontactinfo");

  useEffect(() => {
      const fetchFieldOptions = async () => {
        try {
          const data = await fieldOptionRepository.query({
            content_type: "providercontactinfo",
            field_name: "contact_label"
          });
          setPhoneTypeOptions(data);
        } catch (error) {
          console.log(error);
        }
      };

      if (userRoles.permissions.includes("utilities.view_fieldoptions")) 
        fetchFieldOptions();
  }, [fieldOptionRepository, userRoles.permissions]);

  const addFormFields = () => {
    if (!canAdd) return;
    setFormValues([...formValues, formValues.length]);
  };

  const handleCancelEdit = () => {
    setFormValues([]);
  };

  const removeFormFields = (i) => {
    if (!canDelete) return;
    let newFormValues = [...formValues];
    newFormValues.splice(i, 1);
    setFormValues(newFormValues);
  };

  useEffect(() => {
    const fetchProviderPhones = async () => {
      try {
        const providerPhones = await providerContactInfoRepository.query({
          related_provider: providerDetail.pk,
          contact_type: 1,
          ordering: "created_at"
        });
        setProviderPhones(providerPhones);
        setProviderTypesActive(providerPhones.map(item => item.contact_label));
      } catch (error) {
        console.error("Error fetching provider phones:", error);
      }
    }
    if (providerDetail && canView) fetchProviderPhones();
  }, [providerDetail, providerContactInfoRepository, trigger, userRoles.permissions, canView]);

  const disableAddMore = 
    !canAdd ||
    providerPhones.length === phoneTypeOptions.length ||
    providerPhones.length + formValues.length === phoneTypeOptions.length;

  if (!canView) {
    return null;
  }

  return (
    <div className="w-full mb-4">
      <div className="flex items-center">
        <h2 className="font-bold">Additional Phones:</h2>
        {!(loggedInUser?.entity?.entity_type_label === "Provider" &&
        providerDetail.pk !== activeProvObj[providerDetail.pk]) && (
          <Tooltip
            title={
              disableAddMore
                ? "No additional phone types available"
                : "Add Phone"
            }
          >
            <span>
              <IconButton
                onClick={() => addFormFields()}
                disabled={disableAddMore}
                className="text-green-600"
              >
                <ControlPointOutlined />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>
      {!providerPhones.length ? (
        <p className="text-gray-400 italic">No additional phones added</p>
      ) : (
        providerPhones.map((phone) => (
          <PhoneDisplayRow
            key={phone.id}
            phone={phone}
            provider={providerDetail}
            trigger={trigger}
            setTrigger={setTrigger}
            activeProvObj={activeProvObj}
            phoneTypeOptions={phoneTypeOptions}
            providerTypesActive={providerTypesActive}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        ))
      )}
      {canAdd && formValues.length ?
        formValues.map((_item, idx) => (
            <InlineEditPhone
              key={idx}
              index={idx}
              handleCancelEdit={handleCancelEdit}
              trigger={trigger}
              setTrigger={setTrigger}
              provider={providerDetail}
              phoneTypeOptions={phoneTypeOptions}
              removeFormFields={removeFormFields}
              providerTypesActive={providerTypesActive}
            />
          ))
          : ""
      }
    </div>
  );
}
